import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import DealRoomInquiriesPage from '../components/Succeedo/DealRoom/DealRoomInquiriesPage';

const INQUIRIES_QUERY = gql`
  query DealRoomInquiries {
    currentUser { id name succeedo_persona }
    my_deal_room_inquiries
  }
`;

const APPROVE = gql`
  mutation SellerApproveInquiry($id: ID!) {
    seller_approve_inquiry(introduction_request_id: $id) {
      introduction_request
      errors
    }
  }
`;

const DECLINE = gql`
  mutation SellerDeclineInquiry($id: ID!) {
    seller_decline_inquiry(introduction_request_id: $id) {
      introduction_request
      errors
    }
  }
`;

const SIGN = gql`
  mutation SellerSignNda($id: ID!) {
    seller_sign_nda(introduction_request_id: $id) {
      introduction_request
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function track(event, props = {}) {
  if (typeof window !== 'undefined' && window.ahoy?.track) {
    try { window.ahoy.track(event, props); } catch (_e) {}
  }
}

function InquiriesLoader() {
  const { data, loading, refetch } = useQuery(INQUIRIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const [approving, setApproving] = useState({});
  const [declining, setDeclining] = useState({});
  const [signing, setSigning] = useState({});

  const [approveMutation] = useMutation(APPROVE);
  const [declineMutation] = useMutation(DECLINE);
  const [signMutation]    = useMutation(SIGN);

  const wrap = (id, setter, mutation, eventName) => async () => {
    setter((s) => ({ ...s, [id]: true }));
    try {
      const { data: res } = await mutation({ variables: { id } });
      const key = Object.keys(res || {})[0];
      const errors = res?.[key]?.errors || [];
      if (errors.length) {
        alert(errors.join('\n'));
      } else {
        track(eventName, { introduction_request_id: id });
        await refetch();
      }
    } catch (e) {
      alert(e.message || 'Action failed');
    } finally {
      setter((s) => { const next = { ...s }; delete next[id]; return next; });
    }
  };

  React.useEffect(() => {
    track('succeedo_deal_room_inquiries_viewed');
  }, []);

  const user = data?.currentUser;

  return (
    <DealRoomInquiriesPage
      inquiries={data?.my_deal_room_inquiries || []}
      loading={loading}
      approving={approving}
      declining={declining}
      signing={signing}
      persona={(user?.succeedo_persona || 'seller').toUpperCase()}
      userName={user?.name || ''}
      userInitials={deriveInitials(user?.name)}
      onApprove={(id) => wrap(id, setApproving, approveMutation, 'succeedo_marketplace_seller_approved')()}
      onDecline={(id) => wrap(id, setDeclining, declineMutation, 'succeedo_marketplace_seller_declined')()}
      onSignNda={(id) => wrap(id, setSigning,   signMutation,    'succeedo_marketplace_seller_signed_nda')()}
    />
  );
}

export default function SucceedoDealRoomInquiriesContainer() {
  return (
    <ApolloProvider client={client}>
      <InquiriesLoader />
    </ApolloProvider>
  );
}
